import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, PaperPlaneTilt, CheckCircle, ArrowUUpLeft, Link as LinkIcon, Trash, Lightning, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, money, shortDate, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StageBadge, Field } from "@/components/common";
import DocumentPanel from "@/components/DocumentPanel";
import NotesPanel from "@/components/NotesPanel";
import ProgramPlan from "@/components/ProgramPlan";
import SowPanel from "@/components/SowPanel";
import { DeterminationTab, StructuredBudgetTab, ReimbursementsTab, RenewalTab, CloseoutTab } from "@/components/Phase2Tabs";
import { AmendmentsTab } from "@/components/Phase3Tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const TABS = ["Overview", "Grant Links", "Procurement", "SOW", "Program Plan", "Budget", "Reimbursements", "Documents", "Amendments", "Review History", "Renewal", "Closeout", "Activity"];

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [grants, setGrants] = useState([]);
  const [users, setUsers] = useState([]);
  const [gates, setGates] = useState({ ready: true, missing: [] });
  const [comment, setComment] = useState("");

  const reload = useCallback(async () => {
    const [{ data: contract }, { data: gateData }] = await Promise.all([
      api.get(`/contracts/${id}`),
      api.get(`/contracts/${id}/gates`),
    ]);
    setC(contract);
    setGates(gateData);
  }, [id]);

  useEffect(() => {
    reload();
    api.get("/grants").then((r) => setGrants(r.data));
    api.get("/users").then((r) => setUsers(r.data));
  }, [id, reload]);

  if (!c) return <div className="text-muted-foreground">Loading contract…</div>;

  const isAdmin = ["contract_administrator", "admin"].includes(user.role);
  const stage = c.workflow_stage;

  const submitReview = async () => {
    try {
      await api.post(`/contracts/${id}/submit-review`);
      toast.success("Submitted for Grant Manager review");
      reload();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(formatApiErrorDetail(d));
    }
  };

  const decide = async (decision) => {
    try {
      await api.post(`/contracts/${id}/decision`, { decision, comment });
      toast.success(decision === "approve" ? "Approved & advanced" : "Returned for revision");
      setComment("");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const execute = async () => {
    await api.post(`/contracts/${id}/execute`);
    toast.success("Contract executed");
    reload();
  };

  return (
    <div>
      <button data-testid="back-to-contracts" onClick={() => navigate("/contracts")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors">
        <ArrowLeft size={16} /> Contracts
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6 rise">
        <div>
          <div className="overline text-muted-foreground mb-1">{c.contract_number} · {c.contract_type}</div>
          <h1 className="font-head font-bold text-3xl lg:text-4xl tracking-tight leading-none">{c.title}</h1>
          <div className="flex items-center gap-3 mt-3">
            <StageBadge stage={stage} testid="detail-stage" />
            {c.is_overdue && <span className="text-xs bg-alert/10 text-alert px-2 py-0.5 rounded-sm flex items-center gap-1"><Warning size={12} /> Overdue</span>}
            <span className="text-sm text-muted-foreground">{money(c.amount)}</span>
          </div>
        </div>
      </div>

      <WorkflowBar
        c={c} stage={stage} user={user} isAdmin={isAdmin} gates={gates}
        comment={comment} setComment={setComment}
        onSubmit={submitReview} onDecide={decide} onExecute={execute} reload={reload}
      />

      <Tabs defaultValue="Overview" className="mt-6">
        <TabsList className="w-full justify-start overflow-x-auto flex-nowrap no-scrollbar">
          {TABS.map((t) => <TabsTrigger key={t} value={t} data-testid={`tab-${t.toLowerCase().replace(/ /g, "-")}`}>{t}</TabsTrigger>)}
        </TabsList>

        <TabsContent value="Overview" className="mt-5">
          <OverviewTab c={c} users={users} isAdmin={isAdmin} reload={reload} />
        </TabsContent>

        <TabsContent value="Grant Links" className="mt-5">
          <GrantLinksTab c={c} grants={grants} isAdmin={isAdmin} reload={reload} navigate={navigate} />
        </TabsContent>

        <TabsContent value="Procurement" className="mt-5">
          <DeterminationTab c={c} isAdmin={isAdmin} reload={reload} />
        </TabsContent>

        <TabsContent value="SOW" className="mt-5">
          <Panel><SowPanel contract={c} reload={reload} /></Panel>
        </TabsContent>

        <TabsContent value="Program Plan" className="mt-5">
          <ProgramPlan contractId={c.id} isStaff={true} />
        </TabsContent>

        <TabsContent value="Budget" className="mt-5">
          <StructuredBudgetTab c={c} isAdmin={isAdmin} reload={reload} />
        </TabsContent>

        <TabsContent value="Reimbursements" className="mt-5">
          <ReimbursementsTab c={c} user={user} reload={reload} />
        </TabsContent>

        <TabsContent value="Documents" className="mt-5">
          <Panel><DocumentPanel relatedType="contract" relatedId={c.id} onChange={reload} /></Panel>
        </TabsContent>

        <TabsContent value="Amendments" className="mt-5">
          <AmendmentsTab c={c} isAdmin={isAdmin} reload={reload} />
        </TabsContent>

        <TabsContent value="Review History" className="mt-5">
          <ReviewHistoryTab c={c} />
        </TabsContent>

        <TabsContent value="Renewal" className="mt-5">
          <RenewalTab c={c} isAdmin={isAdmin} reload={reload} />
        </TabsContent>

        <TabsContent value="Closeout" className="mt-5">
          <CloseoutTab c={c} isAdmin={isAdmin} reload={reload} />
        </TabsContent>

        <TabsContent value="Activity" className="mt-5">
          <Panel><NotesPanel relatedType="contract" relatedId={c.id} /></Panel>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Panel({ children }) {
  return <div className="bg-card border border-border rounded-md p-6">{children}</div>;
}

function WorkflowBar({ c, stage, user, isAdmin, gates, comment, setComment, onSubmit, onDecide, onExecute, reload }) {
  const role = user.role;
  const canGM = ["grant_manager", "admin"].includes(role) && stage === "grant_manager_review";
  const canCS = ["contract_specialist", "admin"].includes(role) && stage === "contract_specialist_review";
  const canLegal = ["legal_reviewer", "admin"].includes(role) && stage === "ready_for_legal";
  const canSubmit = isAdmin && stage === "intake";

  if (!canGM && !canCS && !canLegal && !canSubmit) {
    return (
      <div className="bg-primary/5 border border-primary/20 rounded-md px-5 py-3 text-sm text-muted-foreground">
        This record is in <b className="text-foreground">{c.stage_label}</b>. No action is required from your role right now.
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-md p-5">
      <div className="overline text-muted-foreground mb-3">Sequential Review Workflow</div>
      {canSubmit && (
        <div>
          {gates.missing.length > 0 ? (
            <div className="mb-3">
              <div className="text-sm text-alert flex items-center gap-1.5 mb-2"><Warning size={16} /> Workflow gate — complete before submitting:</div>
              <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-0.5">
                {gates.missing.map((m) => <li key={m}>{m}</li>)}
              </ul>
            </div>
          ) : (
            <p className="text-sm text-emerald-700 flex items-center gap-1.5 mb-3"><CheckCircle size={16} weight="fill" /> All required components present.</p>
          )}
          <Button data-testid="submit-review-btn" onClick={onSubmit} disabled={gates.missing.length > 0}>
            <PaperPlaneTilt size={16} className="mr-1.5" /> Submit for Grant Manager Review
          </Button>
        </div>
      )}

      {(canGM || canCS || canLegal) && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {canGM && "Review funding, grant alignment, and related obligations."}
            {canCS && "Review documentation, compliance, and state procedure. 30-day window."}
            {canLegal && "Final legal checkpoint. Complete the legal checklist, then approve to execute. 30-day window."}
            {c.review?.due_at && <span className="ml-1 text-foreground font-medium">Due {shortDate(c.review.due_at)}.</span>}
          </p>
          {canCS && <ReviewChecklist c={c} reload={reload} items={[["compliance", "State procedure & compliance verified"], ["documents", "Required documentation complete"]]} />}
          {canLegal && <ReviewChecklist c={c} reload={reload} items={[["legal_terms", "Legal terms & clauses reviewed"], ["signatures", "Signature authority verified"], ["ready_execute", "Cleared to execute"]]} />}
          <Textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Reviewer note (required for return)…" className="min-h-[60px]" />
          <div className="flex gap-2">
            <Button data-testid="approve-btn" onClick={() => onDecide("approve")}>
              {canLegal ? <><Lightning size={16} className="mr-1.5" /> Approve &amp; Execute</> : <><CheckCircle size={16} className="mr-1.5" /> Approve &amp; Advance</>}
            </Button>
            <Button data-testid="return-btn" variant="outline" onClick={() => onDecide("return")}><ArrowUUpLeft size={16} className="mr-1.5" /> Return for Revision</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewChecklist({ c, reload, items }) {
  const checklist = c.checklist || {};
  const toggle = async (key, value) => {
    await api.put(`/contracts/${c.id}/checklist`, { key, value });
    reload();
  };
  return (
    <div className="border border-border rounded-md p-4 bg-secondary/40 space-y-2.5">
      <div className="overline text-muted-foreground">Review Checklist</div>
      {items.map(([key, label]) => (
        <label key={key} className="flex items-center gap-3 cursor-pointer text-sm">
          <Checkbox data-testid={`checklist-${key}`} checked={!!checklist[key]} onCheckedChange={(v) => toggle(key, !!v)} />
          {label}
        </label>
      ))}
    </div>
  );
}

function OverviewTab({ c, users, isAdmin, reload }) {
  const [edit, setEdit] = useState(false);
  const [gm, setGm] = useState(c.assigned_grant_manager || "");
  const [cs, setCs] = useState(c.assigned_contract_specialist || "");

  const byRole = (r) => users.filter((u) => u.role === r);

  const save = async () => {
    await api.put(`/contracts/${c.id}`, { title: c.title, assigned_grant_manager: gm, assigned_contract_specialist: cs });
    toast.success("Assignments updated");
    setEdit(false);
    reload();
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 bg-card border border-border rounded-md p-6 grid sm:grid-cols-2 gap-5">
        <Field label="Grantee / Vendor">{c.grantee_vendor}</Field>
        <Field label="Program">{c.program}</Field>
        <Field label="Procurement Method">{c.procurement_method}</Field>
        <Field label="Contract Type">{c.contract_type}</Field>
        <Field label="Amount">{money(c.amount)}</Field>
        <Field label="Status">{c.status}</Field>
        <Field label="Start Date">{shortDate(c.start_date)}</Field>
        <Field label="End Date">{shortDate(c.end_date)}</Field>
      </div>
      <div className="bg-card border border-border rounded-md p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div className="overline text-muted-foreground">Assignments</div>
          {isAdmin && <button data-testid="edit-assignments" onClick={() => setEdit(!edit)} className="text-xs text-primary hover:underline">{edit ? "Cancel" : "Edit"}</button>}
        </div>
        {!edit ? (
          <>
            <Field label="Administrator">{c.administrator_name}</Field>
            <Field label="Grant Manager">{c.grant_manager_name}</Field>
            <Field label="Contract Specialist">{c.specialist_name}</Field>
          </>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="overline text-muted-foreground">Grant Manager</Label>
              <Select value={gm} onValueChange={setGm}>
                <SelectTrigger data-testid="assign-gm"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{byRole("grant_manager").map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="overline text-muted-foreground">Contract Specialist</Label>
              <Select value={cs} onValueChange={setCs}>
                <SelectTrigger data-testid="assign-cs"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{byRole("contract_specialist").map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button size="sm" data-testid="save-assignments" onClick={save}>Save</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function GrantLinksTab({ c, grants, isAdmin, reload, navigate }) {
  const [gid, setGid] = useState("");
  const [alloc, setAlloc] = useState("");

  const link = async () => {
    if (!gid) return toast.error("Select a grant");
    await api.post(`/contracts/${c.id}/link-grant`, { grant_id: gid, allocation: parseFloat(alloc) || 0 });
    toast.success("Grant linked");
    setGid(""); setAlloc("");
    reload();
  };
  const unlink = async (grantId) => {
    await api.delete(`/contracts/${c.id}/link-grant/${grantId}`);
    reload();
  };

  return (
    <div className="space-y-5">
      {isAdmin && (
        <div className="bg-card border border-border rounded-md p-5 flex flex-wrap items-end gap-3">
          <div className="space-y-1.5 flex-1 min-w-[220px]">
            <Label className="overline text-muted-foreground">Grant (many-to-many)</Label>
            <Select value={gid} onValueChange={setGid}>
              <SelectTrigger data-testid="link-grant-select"><SelectValue placeholder="Select grant" /></SelectTrigger>
              <SelectContent>{grants.map((g) => <SelectItem key={g.id} value={g.id}>{g.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 w-44">
            <Label className="overline text-muted-foreground">Allocation ($)</Label>
            <Input data-testid="link-allocation" type="number" value={alloc} onChange={(e) => setAlloc(e.target.value)} />
          </div>
          <Button data-testid="link-grant-btn" onClick={link}><LinkIcon size={16} className="mr-1.5" /> Link Grant</Button>
        </div>
      )}
      {(c.linked_grants || []).length === 0 ? (
        <div className="border border-dashed border-border rounded-md p-8 text-center text-sm text-muted-foreground">No grants linked.</div>
      ) : (
        <div className="space-y-2">
          {c.linked_grants.map((gl) => (
            <div key={gl.grant_id} data-testid={`linked-grant-${gl.grant_id}`} className="flex items-center justify-between bg-card border border-border rounded-md p-4">
              <button onClick={() => navigate(`/grants/${gl.grant_id}`)} className="text-left">
                <div className="text-sm font-medium hover:text-primary transition-colors">{gl.grant_title || gl.grant_id}</div>
                <div className="text-xs text-muted-foreground">{gl.funding_source}</div>
              </button>
              <div className="flex items-center gap-4">
                <div className="text-sm font-medium">{money(gl.allocation)}</div>
                {isAdmin && <Button size="icon" variant="ghost" data-testid={`unlink-${gl.grant_id}`} onClick={() => unlink(gl.grant_id)}><Trash size={16} className="text-alert" /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewHistoryTab({ c }) {
  const h = c.review_history || [];
  return (
    <div className="bg-card border border-border rounded-md p-6">
      {h.length === 0 ? (
        <div className="text-sm text-muted-foreground">No review decisions recorded yet.</div>
      ) : (
        <div className="space-y-3">
          {h.map((r, i) => (
            <div key={i} data-testid={`review-hist-${i}`} className="flex items-start gap-3 border-b border-border pb-3 last:border-0">
              {r.decision === "approve" ? <CheckCircle size={18} className="text-emerald-600 mt-0.5" weight="fill" /> : <ArrowUUpLeft size={18} className="text-alert mt-0.5" />}
              <div>
                <div className="text-sm font-medium">{r.role?.replace(/_/g, " ")} · {r.decision === "approve" ? "Approved" : "Returned"}</div>
                <div className="text-xs text-muted-foreground">{r.by} · {r.at?.slice(0, 16).replace("T", " ")}</div>
                {r.comment && <div className="text-sm mt-1">{r.comment}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
